/**
 * ScheduleLinkBanner.tsx — FEAT-2026-0012
 *
 * Persistent banner shown at the top of the Quote / Work Order editor when a
 * scenario is attached to that source.  Summarizes the primary scenario's
 * planned-ness, surfaces "Out of sync" / "Finalized" chips, and offers
 * "View schedule" + "Sync now" actions.
 *
 * G2-4 (audit #2): no longer gated on hasScenario. A source with schedule
 * rows but NO Scenario row (scheduled directly on BASELINE — legacy, API,
 * chat) gets a reduced variant: "scheduled directly on the live board", the
 * same Out-of-sync chip, and Sync now — previously such sources drifted with
 * zero surface. Renders nothing only when the source has no plan AND no
 * schedule rows. Colours come from theme tokens — no hex literals.
 *
 * v1.0.22 redesign: the MUI <Alert> is gone. It forced `.MuiAlert-message`
 * to width:100% beside a non-shrinking action box in a non-wrapping flex row,
 * so "Sync now" rendered past the tinted surface and the page grew a
 * horizontal scrollbar. The banner is now a self-contained card: state icon
 * in a tinted circle, a flexible text column (minWidth: 0), and an action
 * group that wraps UNDER the text at narrow widths — nothing can clip.
 * Copy builders live in scheduleLinkBannerText.ts (pure, unit-tested).
 */

import { Box, Button, Chip, CircularProgress, Tooltip, Typography } from '@mui/material';
import { CalendarMonth, EventRepeat, Sync, WarningAmberRounded, BoltOutlined } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import type { SourceScheduleImpact } from '../../services/scheduleScenario';
import { formatBannerTitle, formatBannerStats, formatStaleDetail } from './scheduleLinkBannerText';

interface ScheduleLinkBannerProps {
  impact: SourceScheduleImpact | null;
  loading?: boolean;
  syncing?: boolean;
  /** Open the scenario in the schedule. Receives the primary scenario key. */
  onView: (scenarioKey?: string) => void;
  /** Re-sync the attached scenario's tasks with the source line items. */
  onSync: () => void;
}

export const ScheduleLinkBanner = ({
  impact,
  loading,
  syncing,
  onView,
  onSync,
}: ScheduleLinkBannerProps) => {
  const theme = useTheme();

  // G2-4: render for any source with a plan OR schedule rows. Only a truly
  // unscheduled source (no scenario, no tasks, no committed work) stays quiet.
  const hasScheduleRows =
    (impact?.totals?.taskCount ?? 0) > 0 || (impact?.sourceCommittedBookings ?? 0) > 0;
  if (!impact || (!impact.hasScenario && !hasScheduleRows)) return null;

  const primary = impact.scenarios[0];
  // BASELINE-only committed work has no "finalized scenario" but is just as live.
  const showLiveChip =
    impact.hasFinalized || (!impact.hasScenario && (impact.sourceCommittedBookings ?? 0) > 0);
  const isStale = !!impact.stale?.isStale;

  // State tint: calm sage when in sync, warm amber when the schedule drifted.
  const tone = isStale ? theme.palette.warning.main : theme.palette.primary.main;
  const toneInk = isStale ? theme.palette.warning.dark : theme.palette.primary.main;
  // In-sync + plan attached → EventRepeat (linked, re-syncable). In-sync
  // without a plan (live board only) → CalendarMonth. Stale → warning.
  const StateIcon = isStale ? WarningAmberRounded : impact.hasScenario ? EventRepeat : CalendarMonth;

  const title = formatBannerTitle(impact);
  const stats = formatBannerStats(impact);
  const staleDetail = formatStaleDetail(impact.stale);

  return (
    <Box
      sx={{
        mb: 2,
        px: 2,
        py: 1.75,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        columnGap: 1.75,
        rowGap: 1.25,
        borderRadius: 3, // 12px — theme.shape.borderRadius (4) × 3
        border: `1px solid ${alpha(tone, 0.3)}`,
        bgcolor: alpha(tone, 0.08),
      }}
    >
      {/* State icon in a soft tinted circle */}
      <Box
        aria-hidden
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(tone, 0.15),
          color: toneInk,
        }}
      >
        <StateIcon fontSize="small" />
      </Box>

      {/* Flexible text column — minWidth: 0 so it shrinks instead of pushing
          the actions out; basis 300px so the actions wrap below when narrow. */}
      <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1, rowGap: 0.5 }}>
          <Typography component="span" sx={{ fontWeight: 600, fontSize: '0.9rem', lineHeight: 1.4, color: 'text.primary' }}>
            {title}
          </Typography>
          {showLiveChip && (
            <Chip
              size="small"
              icon={<BoltOutlined sx={{ fontSize: 14 }} />}
              label="Finalized / live"
              sx={{
                height: 22,
                fontWeight: 600,
                fontSize: '0.72rem',
                color: theme.schedule.committed,
                bgcolor: alpha(theme.schedule.committed, 0.12),
                border: `1px solid ${alpha(theme.schedule.committed, 0.4)}`,
                '& .MuiChip-icon': { color: theme.schedule.committed },
              }}
            />
          )}
          {isStale && (
            <Tooltip title="Run Sync now to bring the schedule back in line with this source.">
              <Chip
                size="small"
                icon={<WarningAmberRounded sx={{ fontSize: 14 }} />}
                label={`Out of sync — ${staleDetail}`}
                sx={{
                  height: 22,
                  maxWidth: '100%',
                  fontWeight: 600,
                  fontSize: '0.72rem',
                  color: theme.palette.warning.dark,
                  bgcolor: alpha(theme.palette.warning.main, 0.16),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.45)}`,
                  '& .MuiChip-icon': { color: theme.palette.warning.dark },
                }}
              />
            </Tooltip>
          )}
        </Box>
        {stats && (
          <Typography sx={{ mt: 0.25, fontSize: '0.78rem', color: 'text.secondary' }}>
            {stats}
          </Typography>
        )}
      </Box>

      {/* Actions — inside the card; ml:auto keeps them right-aligned on wide
          rows, flexWrap on the card drops them below the text when narrow. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', ml: 'auto' }}>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          startIcon={<CalendarMonth fontSize="small" />}
          onClick={() => onView(primary?.scenarioKey)}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            color: 'text.primary',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            '&:hover': { borderColor: 'text.secondary', bgcolor: 'background.paper' },
          }}
        >
          View schedule
        </Button>
        <Tooltip
          title={
            isStale
              ? 'Re-generate tasks so the schedule matches the current line items.'
              : 'Re-sync the schedule with the current line items.'
          }
        >
          <span>
            <Button
              size="small"
              variant="contained"
              disableElevation
              color={isStale ? 'warning' : 'primary'}
              startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : <Sync fontSize="small" />}
              onClick={onSync}
              disabled={!!syncing || !!loading}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              {syncing ? 'Syncing…' : 'Sync now'}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default ScheduleLinkBanner;
